import { Component, inject, OnInit, signal } from '@angular/core'

import { RouterLink } from '@angular/router'
import { TreeNode, PrimeTemplate } from 'primeng/api'

import { Tooltip } from 'primeng/tooltip'
import { Tree } from 'primeng/tree'
import { forEach, isMatch, remove } from 'lodash-es'

import { ElasticApi } from '@app/apis/elastic.api'
import { TitleService } from '@app/services/title.service'

type FacetConfig = {
  label: string
  data: {
    facet: string
    queryField: string
  }
}

const FACETS: FacetConfig[] = [
  {
    label: $localize`:@@search-tree-country:Country`,
    data: {
      facet: 'country_label_en_facet',
      queryField: 'country_label_en',
    },
  },
  {
    label: $localize`:@@search-tree-place:Place`,
    data: {
      facet: 'place_facet',
      queryField: 'place_name',
    },
  },
  {
    label: $localize`:@@search-tree-department:Department`,
    data: {
      facet: 'department_code_facet',
      queryField: 'department_code',
    },
  },
  {
    label: $localize`:@@search-tree-year:Year`,
    data: {
      facet: 'date_year_facet',
      queryField: 'date_year',
    },
  },
  {
    label: $localize`:@@search-tree-month:Month`,
    data: {
      facet: 'date_month_facet',
      queryField: 'date_month',
    },
  },
  {
    label: $localize`:@@search-tree-balzac-code:Balzac code`,
    data: {
      facet: 'balzac_saga_name_facet',
      queryField: 'balzac_saga_name',
    },
  },
]

@Component({
  selector: 'app-search',
  templateUrl: 'search-tree.component.html',
  imports: [
    Tree,
    PrimeTemplate,
    Tooltip,
    RouterLink,
  ],
})
export class SearchTreeComponent implements OnInit {
  protected readonly titleService = inject(TitleService)
  protected readonly elasticApi = inject(ElasticApi)

  public results: TreeNode[] = []
  public loading = signal(false)
  public facetList: FacetConfig[] = FACETS
  public nodes = signal<TreeNode[]>([])

  ngOnInit (): void {
    this.titleService.setPageTitle($localize`:@@search-list-page-title:Acted Archives`)

    const rootNode = this.createRootNode()
    this.nodes.set([rootNode])

    // simulate the user click on the root node to get them started
    rootNode.expanded = true
    this.nodeExpand({ node: rootNode })
  }

  private createRootNode (): TreeNode {
    return {
      key: 'root',
      leaf: false,
      children: [],
      ...(this.facetList[0]),
    }
  }

  /**
   * return a dict containing the query params used by the given node, and all its parent nodes
   */
  getRecursiveSearchParams (node: TreeNode, params: { [key: string]: string }): { [key: string]: string } {
    if (node.data?.queryValue) {
      params[node.data?.queryField] = node.data.queryValue
    }
    if (node.parent?.data?.queryValue) {
      return this.getRecursiveSearchParams(node.parent, params)
    }
    return params
  }

  /**
   * Expanding a node adds children (dynamically) so closing must remove them otherwise they
   * accumulate between open / close / open events
   */
  nodeCollapse (event: { node: TreeNode, originalEvent?: Event }) {
    const targetNode = this.getNodeRef(event.node) ?? event.node
    this.persistNodeAsClosed(targetNode)
    this.setNodeChildren(targetNode, [], false)
  }

  private persistNodeAsOpen (node: TreeNode) {
    // get the dict describing the search params for this node
    const searchParams = this.getRecursiveSearchParams(node, {})
    // root node is never persisted
    if (Object.keys(searchParams).length === 0) return
    // get a string representation of the various search params behind the current list of open nodes
    const currentNodesOpen: string[] = JSON.parse(localStorage.getItem('searchTreeNodesOpen') ?? '[]')
    // remove it from the currently open nodes if it already exists, to avoid adding it twice
    remove(currentNodesOpen, (itemValue: string) => itemValue === JSON.stringify(searchParams))
    // now add it to the list
    currentNodesOpen.push(JSON.stringify(searchParams))
    // save the list of open nodes to local storage
    localStorage.setItem('searchTreeNodesOpen', JSON.stringify(currentNodesOpen))
  }

  private persistNodeAsClosed (node: TreeNode) {
    // get the dict describing the search params for this node
    const searchParams = this.getRecursiveSearchParams(node, {})
    // root node is never persisted
    if (Object.keys(searchParams).length === 0) return
    // get a string representation of the various search params behind the current list of open nodes
    const currentNodesOpen: string[] = JSON.parse(localStorage.getItem('searchTreeNodesOpen') ?? '[]')
    // start by removing the node to be closed
    remove(currentNodesOpen, (itemValue: string) => itemValue === JSON.stringify(searchParams))
    // now remove all the child nodes of the node just closed (i.e. those with a superset of our closed node's keys)
    remove(currentNodesOpen, (itemValue: string) => {
      const currentNodeOpenParams = JSON.parse(itemValue)
      return isMatch(currentNodeOpenParams, searchParams)
    })
    // save the list of open nodes to local storage
    localStorage.setItem('searchTreeNodesOpen', JSON.stringify(currentNodesOpen))
  }

  nodeShouldOpen (node: TreeNode) {
    // get the dict describing the search params for this node
    const searchParams = this.getRecursiveSearchParams(node, {})
    // root node is never persisted
    if (Object.keys(searchParams).length === 0) return false
    // get a string representation of the various search params behind the current list of open nodes
    const currentNodesOpen: string[] = JSON.parse(localStorage.getItem('searchTreeNodesOpen') ?? '[]')
    // return whether the current persisted list of open nodes contains this particular node
    // (and hence should be opened)
    return currentNodesOpen.indexOf(JSON.stringify(searchParams)) > -1
  }

  /**
   * A node is expanded, query the server for documents matching the query stored in the node data
   */
  nodeExpand (event: { node: TreeNode, originalEvent?: Event }) {
    if (!event.node) return

    const resolvedNode = this.getNodeRef(event.node)
    const originalNode = resolvedNode ?? event.node
    this.loading.set(true)

    // get search params from this code and the upstream nodes
    const searchParams = this.getRecursiveSearchParams(originalNode, {})
    this.persistNodeAsOpen(originalNode)

    // now get our current and next facetList objects, containing the field query config
    const facetIndex = originalNode.data.index || 0
    const currentFacet = this.facetList[facetIndex]
    const nextFacetIndex = facetIndex + 1
    const nextFacet = this.facetList[nextFacetIndex]

    // there are 2 cases: we are still in 'folder mode', where we present the facetList 'folders',
    // and when there are no more facetLists, we present the resulting documents
    if (this.facetList.length >= nextFacetIndex) {
      this.loadFacetChildren(originalNode, currentFacet, nextFacetIndex, nextFacet, searchParams)
    } else {
      this.loadResultChildren(originalNode, searchParams)
    }
  }

  private loadFacetChildren (
    node: TreeNode,
    currentFacet: FacetConfig,
    nextFacetIndex: number,
    nextFacet: FacetConfig | undefined,
    searchParams: { [key: string]: string },
  ) {
    // ask elastic for the aggregated (facet) data, with the accumulated filter
    this.elasticApi.getFacet(node.data.facet, searchParams).subscribe({
      next: (res: any) => {
        const facetKey = node.data.facet
        const facetFilter = res?.facets?.['_filter_' + facetKey]
        const buckets = facetFilter?.[facetKey]?.buckets ?? []
        const children = this.buildFacetChildren(node, currentFacet, nextFacetIndex, nextFacet, buckets)

        forEach(children, (child: TreeNode) => {
          if (this.nodeShouldOpen(child)) {
            this.nodeExpand({ node: child })
            child.expanded = true
          }
        })

        this.setNodeChildren(node, children, true)
        this.loading.set(false)
      },
      error: (error: any) => {
        console.error('Search tree: facet request failed', { facetKey: node.data.facet, searchParams, error })
        this.loading.set(false)
      },
    })
  }

  private buildFacetChildren (
    parent: TreeNode,
    currentFacet: FacetConfig,
    nextFacetIndex: number,
    nextFacet: FacetConfig | undefined,
    buckets: Array<{ key: string }>,
  ): TreeNode[] {
    return buckets.map(bucket => {
      const child: TreeNode = {
        key: `${parent.key ?? 'root'}/${encodeURIComponent(String(bucket.key))}`,
        label: currentFacet.label + ': ' + bucket.key,
        data: {
          index: nextFacetIndex,
          queryField: currentFacet.data.queryField,
          queryValue: bucket.key,
        },
        leaf: false,
        children: [],
        parent,
      }
      if (nextFacet) {
        child.data.facet = nextFacet.data.facet
      }
      return child
    })
  }

  private loadResultChildren (node: TreeNode, searchParams: { [key: string]: string }) {
    // for the case there are no more facetList configs, we present the documents matching the accumulated query
    this.elasticApi.list(searchParams).subscribe({
      next: (res: any) => {
        const children: TreeNode[] = []
        // loop over search results, creating nodes for each
        forEach(res.results, (result: any) => {
          children.push(this.createResultNode(node, result))
        })
        this.setNodeChildren(node, children, true)
        this.loading.set(false)
      },
      error: (error: any) => {
        console.error('Search tree: list request failed', { searchParams, error })
        this.loading.set(false)
      },
    })
  }

  private createResultNode (parent: TreeNode, result: any): TreeNode {
    return {
      key: result.uuid,
      label: result.uuid,
      icon: 'fa fa-regular fa-file-pdf',
      data: result,
      leaf: true,
      type: 'pdf-result',
      parent,
    }
  }

  private setNodeChildren (node: TreeNode, children: TreeNode[], expanded: boolean) {
    if (node.key) {
      this.nodes.update(nodes => this.updateNodeByKey(nodes, node.key!, current => ({
        ...current,
        expanded,
        children,
      })))
      return
    }
    node.expanded = expanded
    node.children = children
    this.nodes.set([...this.nodes()])
  }

  private getNodeRef (node: TreeNode): TreeNode | null {
    if (!node.key) return null
    return this.findNodeByKey(this.nodes(), node.key)
  }

  private findNodeByKey (nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) return node
      if (node.children?.length) {
        const found = this.findNodeByKey(node.children, key)
        if (found) return found
      }
    }
    return null
  }

  private updateNodeByKey (
    nodes: TreeNode[],
    key: string,
    updater: (node: TreeNode) => TreeNode,
  ): TreeNode[] {
    let changed = false
    const updated = nodes.map(node => {
      if (node.key === key) {
        changed = true
        return updater(node)
      }
      if (node.children?.length) {
        const updatedChildren = this.updateNodeByKey(node.children, key, updater)
        if (updatedChildren !== node.children) {
          changed = true
          return { ...node, children: updatedChildren }
        }
      }
      return node
    })
    return changed ? updated : nodes
  }
}
